import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

const contactFormSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
  email: z.string().email("Valid email is required").max(255),
  phone: z.string().min(1, "Phone number is required").max(50),
  message: z.string().min(1, "Message is required").max(1000),
  // Honeypot field - should always be empty (bots fill this)
  website: z.string().max(0, "").optional(),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    // Honeypot check - if website field has value, it's a bot
    if (data.website) {
      // Silently reject but show success to not alert the bot
      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Remove honeypot field before sending
      const { website, ...cleanData } = data;
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "contact",
          data: cleanData,
          language: "en",
        },
      });

      if (error) throw error;

      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
      });

      reset();
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto relative">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-display font-light text-foreground mb-4 tracking-widest">
          QUESTIONS?
        </h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-muted-foreground uppercase text-xs tracking-wide">
            First Name
          </Label>
          <Input
            id="firstName"
            {...register("firstName")}
            className="bg-muted/30 border-border/50 h-12 text-base"
            disabled={isSubmitting}
          />
          {errors.firstName && (
            <p className="text-sm text-destructive">{errors.firstName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-muted-foreground uppercase text-xs tracking-wide">
            Last Name
          </Label>
          <Input
            id="lastName"
            {...register("lastName")}
            className="bg-muted/30 border-border/50 h-12 text-base"
            disabled={isSubmitting}
          />
          {errors.lastName && (
            <p className="text-sm text-destructive">{errors.lastName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-muted-foreground uppercase text-xs tracking-wide">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            className="bg-muted/30 border-border/50 h-12 text-base"
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-muted-foreground uppercase text-xs tracking-wide">
            Phone Number
          </Label>
          <Input
            id="phone"
            type="tel"
            {...register("phone")}
            className="bg-muted/30 border-border/50 h-12 text-base"
            disabled={isSubmitting}
          />
          {errors.phone && (
            <p className="text-sm text-destructive">{errors.phone.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="message" className="text-muted-foreground uppercase text-xs tracking-wide">
            Message
          </Label>
          <Textarea
            id="message"
            {...register("message")}
            className="bg-muted/30 border-border/50 min-h-[200px] text-base"
            disabled={isSubmitting}
          />
          {errors.message && (
            <p className="text-sm text-destructive">{errors.message.message}</p>
          )}
        </div>

        {/* Honeypot field - hidden from users, bots will fill it */}
        <div className="absolute -left-[9999px]" aria-hidden="true">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            type="text"
            {...register("website")}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <Button
          type="submit"
          className="w-full h-14 text-lg tracking-widest bg-gradient-andean hover:opacity-90 transition-opacity uppercase"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Sending...
            </>
          ) : (
            "SEND"
          )}
        </Button>
      </form>
    </div>
  );
}
